import { useCallback, useEffect, useState } from 'react';
import adminService from '../../api/adminService';
import useAuth from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import StatusBadge from '../common/StatusBadge';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { fullName, dateOnly } from '../../utils/formatters';

const ROLE_FILTERS = ['', 'CLIENT', 'TECHNICIAN', 'APPROVER', 'ADMIN'];
const ASSIGNABLE_ROLES = ['CLIENT', 'APPROVER', 'ADMIN']; // TECHNICIAN is set via registration only
const NEXT_STATUS = { ACTIVE: 'SUSPENDED', SUSPENDED: 'ACTIVE', PENDING: 'ACTIVE', INACTIVE: 'ACTIVE', REJECTED: 'ACTIVE' };

export default function UserManagement() {
  const notify = useNotifications();
  const { user: me } = useAuth();
  const [role, setRole] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminService.listUsers({ page, size: 15, ...(role ? { role } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, role, notify]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (u, status) => {
    setBusy(u.id);
    try {
      await adminService.updateUserStatus(u.id, status);
      notify.success(`${fullName(u)} → ${status.toLowerCase()}`);
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  const setUserRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (!window.confirm(`Change ${fullName(u)}'s role from ${u.role} to ${newRole}?`)) { load(); return; }
    setBusy(u.id);
    try {
      await adminService.updateUserRole(u.id, newRole);
      notify.success(`${fullName(u)} is now ${newRole}`);
      load();
    } catch (e) { notify.apiError(e); load(); } finally { setBusy(null); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${fullName(u)} permanently?`)) return;
    setBusy(u.id);
    try {
      await adminService.deleteUser(u.id);
      notify.success('User deleted');
      load();
    } catch (e) {
      if (/force=true/.test(e?.message || '')) {
        if (window.confirm(`${fullName(u)} has bookings/reviews. Delete the user AND all their bookings and reviews?`)) {
          try {
            await adminService.deleteUser(u.id, true);
            notify.success('User and related data deleted');
            load();
          } catch (e2) { notify.apiError(e2); }
        }
      } else {
        notify.apiError(e);
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <h1>Users &amp; roles</h1>
        <div className="toolbar">
          {ROLE_FILTERS.map((r) => (
            <button key={r || 'all'} className={`chip ${role === r ? 'active' : ''}`}
              onClick={() => { setRole(r); setPage(0); }}>{r || 'All'}</button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="people" title="No users" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th></th></tr>
              </thead>
              <tbody>
                {data.content.map((u) => {
                  const isSelf = me?.id === u.id;
                  const canReassign = !isSelf && u.role !== 'TECHNICIAN';
                  return (
                    <tr key={u.id}>
                      <td>{fullName(u)}{isSelf && <span className="muted"> (you)</span>}</td>
                      <td>{u.email}</td>
                      <td>
                        {canReassign ? (
                          <select
                            value={u.role}
                            disabled={busy === u.id}
                            onChange={(e) => setUserRole(u, e.target.value)}
                          >
                            {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : (
                          <span className="badge gray">{u.role}</span>
                        )}
                      </td>
                      <td><StatusBadge status={u.status} /></td>
                      <td>{dateOnly(u.createdAt)}</td>
                      <td>
                        {!isSelf && u.role !== 'ADMIN' && (
                          <div className="row" style={{ gap: '.4rem' }}>
                            {NEXT_STATUS[u.status] && (
                              <button
                                className={`btn sm ${u.status === 'ACTIVE' ? 'danger' : 'accent'}`}
                                disabled={busy === u.id}
                                onClick={() => setStatus(u, NEXT_STATUS[u.status])}
                              >
                                {u.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                              </button>
                            )}
                            <button className="btn ghost sm" disabled={busy === u.id} onClick={() => remove(u)}>
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
